import React, { useEffect, useState } from 'react';

import BottomSheet from 'components/BottomSheet';
import { SearchBar } from 'components/Search/styles';
import useStoreTranslation from 'hooks/useStoreTranslation';

import { CloseButton, Header, IconClose, Options, OptionsItem } from './styles';

export interface Data {
  id?: string;
  name: string;
  code?: string;
}

export interface Option {
  icon?: any;
  name: string;
  value: any;
}

interface Props {
  style?: any;
  show: boolean;
  title: string;
  options?: Option[] | [];
  data?: Data[] | [];
  onClose: () => void;
  onChange: (value: any) => void;
  enableSearch?: boolean;
  zIndex?: number;
  noHeader?: boolean;
  modal?: boolean;
}

const renderData = (data: Data[], callBack: (newValue: any) => void) => (
  <Options>
    {data?.map((element) => (
      <OptionsItem key={element.id} onClick={() => callBack(element.id)}>
        <OptionsItem.Name>{element.name}</OptionsItem.Name>
        <OptionsItem.SubTitle>{element.code}</OptionsItem.SubTitle>
      </OptionsItem>
    ))}
  </Options>
);

const renderOptions = (options: Option[], callBack: (newValue: any) => void) => (
  <Options id="test-options">
    {options?.map((option, index) => (
      <OptionsItem key={index} onClick={() => callBack(option.value)}>
        <OptionsItem.Icon>{option.icon}</OptionsItem.Icon>
        <OptionsItem.Name>{option.name}</OptionsItem.Name>
      </OptionsItem>
    ))}
  </Options>
);

const BottomSheetOptions = ({
  options,
  style,
  data,
  onChange,
  onClose,
  enableSearch,
  zIndex,
  noHeader,
  title,
  modal,
  ...rest
}: Props) => {
  const [value, setValue] = useState();
  const [query, setQuery] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const { t } = useStoreTranslation();

  const handleOptionsItemClick = (newValue: any) => {
    if (newValue !== value) {
      setValue(newValue);
      onChange(newValue);
    }
    onClose();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setQuery(value);

    // @ts-ignore
    setFilteredData(data?.filter((d) => d.name.sanitize().toLowerCase().includes(value.sanitize().toLowerCase()) || d.code?.sanitize().toLowerCase().includes(value.sanitize().toLowerCase())));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const ESC_KEY_CODE = 27;
    const escWasPressed = event.keyCode === ESC_KEY_CODE;

    if (escWasPressed) clearSearch();
  };

  const clearSearch = () => {
    setQuery('');
    setFilteredData(data);
  };

  useEffect(() => {
    if (filteredData?.length === 0) {
      setFilteredData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <>
      <BottomSheet
        {...rest}
        noHeader={noHeader}
        modal={modal}
        onClose={onClose}
        noPadding
        style={style}
        zIndex={zIndex}
      >
        {noHeader && (
          <Header>
            <Header.Title>{title}</Header.Title>
            <Header.Close>
              <CloseButton onClick={onClose}>
                <IconClose />
              </CloseButton>
            </Header.Close>
          </Header>
        )}
        {enableSearch && (
          <SearchBar fullWidth={true}>
            <SearchBar.Icon
              src={query ? '/corporate/images/search-bar/active.svg' : '/corporate/images/search-bar/disabled.svg'}
            />
            <SearchBar.InputContainer>
              <SearchBar.Input
                disabled={false}
                onChange={handleSearch}
                onKeyDown={handleKeyDown}
                value={query}
                placeholder={t('searchBar.placeholder')}
              />
            </SearchBar.InputContainer>
            {query.length > 0 && (
              <SearchBar.CloseSearch onClick={clearSearch} src="/corporate/images/search-bar/input-clean.svg" />
            )}
          </SearchBar>
        )}
        {options ? (
          renderOptions(options, handleOptionsItemClick)
        ) : filteredData ? (
          renderData(filteredData, handleOptionsItemClick)
        ) : (
          <></>
        )}
      </BottomSheet>
    </>
  );
};

export default BottomSheetOptions;
